// «Почта» (R-M6): the mail client of the team laid out as HomeMail — mailboxes and folders | the list of letters | the chain being
// read with the CRM records beside it; compose in a window at the bottom right; the keyboard as in HomeMail.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Id } from '@confdeck/core-shared';
import { navigate, useLocation } from '../../shell/router.ts';
import { useSync } from '../../sync/react.tsx';
import { ListReader } from '../../ui/ListReader.tsx';
import { Compose } from './Compose.tsx';
import { ConnectDialog } from './ConnectDialog.tsx';
import { draftFrom, newDraft, type Draft, type ReplyMode } from './drafts.ts';
import { LetterList } from './LetterList.tsx';
import { inView, mark, matches, move, senderOf, type Letter } from './model.ts';
import { HERE_LIMIT, openSearch, useScreenSearch, type ScreenHit } from '../../shell/screen-search.ts';
import { Reader } from './Reader.tsx';
import { setMailNav, useMailNav } from './nav-state.ts';
import { useShortcuts } from './shortcuts.ts';
import { useLetters, useMailboxes } from './use-letters.ts';

export function MailScreen() {
  const client = useSync();
  const letters = useLetters();
  const boxes = useMailboxes();
  const { box, view, ask, n } = useMailNav();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<Id | null>(null);
  const [checked, setChecked] = useState<Set<Id>>(new Set());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [connect, setConnect] = useState<Id | 'new' | null>(null);
  const [help, setHelp] = useState(false);

  const inBox = useMemo(() => letters.filter((l) => box === 'all' || l.mailboxId === box), [letters, box]);
  // A search looks through every folder but the trash and spam, like HomeMail; without it — the chosen folder.
  const list = useMemo(() => inBox.filter((l) => (query.trim() ? l.folder !== 'trash' && l.folder !== 'junk' : inView(l, view)) && matches(l, query)), [inBox, view, query]);
  const open = letters.find((l) => l.id === openId) ?? null;

  useEffect(() => { setChecked(new Set()); }, [view, box, query]);
  // Another folder chosen in the column: its list starts without a search and without an open letter.
  // A letter opened by its address chooses its folder itself and stays open.
  const letterPlace = useRef<string | null>(null);
  useEffect(() => {
    const kept = letterPlace.current === `${box}|${view}`;
    letterPlace.current = null;
    if (!kept) { setQuery(''); setOpenId(null); }
  }, [view, box]);

  // From a record's card: «?letter=» opens that chain in its folder, «?to=» starts a letter to the address.
  const { search } = useLocation();
  const wantLetter = search.get('letter'), wantTo = search.get('to');
  useEffect(() => {
    if (wantLetter) {
      const l = letters.find((x) => x.id === wantLetter);
      if (!l) return;
      const folder = l.folder === 'drafts' ? 'inbox' : l.folder;
      letterPlace.current = `all|${folder}`;
      setMailNav({ box: 'all', view: folder }); setQuery(''); setOpenId(l.id);
      if (l.unread) mark(client, [l.id], { unread: false });
    } else if (wantTo) {
      const mb = boxes[0];
      if (!mb) return;
      setDraft({ ...newDraft(mb.id, mb.signature), to: wantTo });
    } else return;
    navigate('/s/mail', { replace: true });
  }, [wantLetter, wantTo, letters, boxes, client]);

  const openLetter = useCallback(async (l: Letter) => {
    if (l.folder === 'drafts') { setDraft(await draftFrom(l)); return; }
    setOpenId(l.id);
    if (l.unread) mark(client, [l.id], { unread: false });
  }, [client]);

  const defaultBox = (box !== 'all' ? box : open?.mailboxId) ?? boxes[0]?.id ?? null;
  const compose = useCallback(() => { if (defaultBox) setDraft(newDraft(defaultBox, boxes.find((b) => b.id === defaultBox)?.signature ?? '')); else setConnect('new'); }, [defaultBox, boxes]);
  const reply = useCallback(async (l: Letter, mode: ReplyMode) => {
    const mb = boxes.find((b) => b.id === l.mailboxId) ?? boxes[0];
    if (mb) setDraft(await draftFrom(l, mode, mb));
  }, [boxes]);

  // «Новое письмо» stands at the bottom of the left column and the mailbox settings in it: the column asks, the screen opens the window.
  useEffect(() => {
    if (!ask) return;
    if (ask.kind === 'compose') compose(); else setConnect(ask.id ?? 'new');
    setMailNav({ ask: null });
  }, [n]);

  // The global search looks here first (R-U23): letters of the open mailbox by the mail's own rules (from:, to:, has:, is:), every
  // folder but the trash and spam; «Все письма по …» leaves the found ones in the list, as the search field of the list did.
  useScreenSearch((q) => {
    const found = inBox.filter((l) => l.folder !== 'trash' && l.folder !== 'junk' && matches(l, q));
    const hits: ScreenHit[] = found.slice(0, HERE_LIMIT - 1).map((l) => ({ id: l.id, recordId: l.id, label: l.subject || '(без темы)', hint: senderOf(l), icon: 'mail', run: () => { setQuery(''); void openLetter(l); } }));
    if (found.length) hits.push({ id: `all:${q}`, label: `Все письма по «${q.trim()}»`, hint: String(found.length), icon: 'search', run: () => setQuery(q.trim()) });
    return hits;
  });

  const targets = checked.size ? [...checked] : open ? [open.id] : [];
  const afterMove = (ids: Id[]) => {
    // The next letter of the list opens in place of the one that left the folder (HomeMail).
    if (open && ids.includes(open.id)) { const i = list.findIndex((l) => l.id === open.id); const next = list.slice(i + 1).find((l) => !ids.includes(l.id)) ?? list.slice(0, i).reverse().find((l) => !ids.includes(l.id)); setOpenId(next?.id ?? null); }
    setChecked(new Set());
  };
  const moveTo = (ids: Id[], folder: 'inbox' | 'archive' | 'trash' | 'junk') => { move(client, ids, folder); afterMove(ids); };

  useShortcuts(!draft && !connect, {
    list, open, targets,
    onOpen: (l) => void openLetter(l), onClose: () => (checked.size ? setChecked(new Set()) : setOpenId(null)),
    onToggle: (id) => setChecked((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; }),
    onMove: moveTo, onMark: (ids, c) => mark(client, ids, c), onReply: (l, m) => void reply(l, m), onCompose: compose, onSearch: openSearch, onHelp: () => setHelp((h) => !h),
  });

  return (
    <>
      <ListReader reading={!!open} onBack={() => setOpenId(null)} listLabel="Список писем" readerLabel="Письмо"
        list={<LetterList list={list} view={view} query={query} onQuery={setQuery} openId={openId} checked={checked} onChecked={setChecked}
          onOpen={(l) => void openLetter(l)} onMove={moveTo} onMark={(ids, c) => mark(client, ids, c)} boxes={boxes} box={box} />}
        reader={<Reader letter={open} all={letters} help={help} onReply={(l, m) => void reply(l, m)} onMove={moveTo}
          onMark={(ids, c) => mark(client, ids, c)} />} />
      {draft && <Compose key={draft.id} initial={draft} boxes={boxes} onClose={() => setDraft(null)} />}
      {connect && <ConnectDialog mailbox={connect === 'new' ? null : boxes.find((b) => b.id === connect) ?? null} onClose={() => setConnect(null)} />}
    </>
  );
}
